import type {
  Modification,
  NavigationEditSchemaType,
} from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useContext, useMemo, useState } from 'react';
import { RuleDeletionAlert } from '../RuleDeletionAlert';
import {
  RulesContent,
  type RulesContentListProps,
  RulesManager,
  RulesManagerEmptySearch,
  RulesManagerFilters,
  useRulesManagerFilters,
} from '../RulesManager';
import type { NavigationManagerProps } from './NavigationManager';
import { NavigationRuleListItem } from './NavigationRuleListItem';
import { NavigationRulePreview } from './NavigationRulePreview';
import { NavigationRulesContext } from './NavigationRulesManagerContext';

export type NavigationRulesContentProps = Pick<
  NavigationManagerProps,
  | 'deleteNavigationModification'
  | 'reorderNavigationModification'
  | 'editNavigationModification'
>;

export type Filters = 'all' | 'link' | 'button';

export const NavigationRulesContent = ({
  deleteNavigationModification,
  reorderNavigationModification,
  editNavigationModification,
}: NavigationRulesContentProps) => {
  const { rules } = useContext(NavigationRulesContext);
  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();
  const [isDraggingAllowed, setIsDraggingAllowed] = useBoolean(true);

  const [ruleToDelete, setRuleToDelete] =
    useState<Modification<NavigationEditSchemaType.blockInteractions> | null>(
      null
    );

  const filterOptions = useMemo(
    () => ({
      initialFilter: 'all' as Filters,
      searchKeys: [
        {
          name: 'vlaue',
          getFn: (rule) => rule.content.blockingValue,
        },
      ],
      dataSet: rules,
      filterFunc: (
        rule: Modification<NavigationEditSchemaType.blockInteractions>,
        filter: Filters
      ) => {
        if (filter === 'all') return true;
        return rule.content.blockingType === filter;
      },
    }),
    [rules]
  );

  const {
    setSearchValue,
    searchValue,
    filter,
    setFilter,
    filteredList,
    isFiltersActive,
  } = useRulesManagerFilters<
    Filters,
    Modification<NavigationEditSchemaType.blockInteractions>
  >(filterOptions);

  const onReorderModifications: RulesContentListProps<
    Modification<NavigationEditSchemaType.blockInteractions>
  >['onOrderChange'] = async ({ rules, destinationIndex }) => {
    const moved = rules[destinationIndex].rule;
    const after = rules[destinationIndex + 1]?.rule;
    await reorderNavigationModification(moved, after).catch(() => {
      notify.snackbar({
        type: 'error',
        label: 'Failed to reorder. Please try again',
      });
    });
  };

  return (
    <>
      <RulesContent>
        <RulesManagerFilters<Filters>
          onSearchChange={setSearchValue}
          onFiltersChange={setFilter}
          dropdownItems={[
            { id: 'link', label: 'Link' },
            { id: 'button', label: 'Button' },
            { id: 'all', label: 'Any type' },
          ]}
          searchValue={searchValue}
          selectedFilter={filter}
        />
        <RulesManager.Content>
          {filteredList.length ? (
            <RulesContent.List<
              Modification<NavigationEditSchemaType.blockInteractions>
            >
              onOpenRule={setIsDraggingAllowed.setFalse}
              onCloseRule={setIsDraggingAllowed.setTrue}
              draggable={!isFiltersActive && isDraggingAllowed}
              onOrderChange={onReorderModifications}
              rules={filteredList.map((rule) => ({
                id: rule.id,
                rule,
              }))}
              renderItem={({ ruleItem, open, openRule, closeRule }) => (
                <NavigationRuleListItem
                  onOpenRule={openRule}
                  onEdit={editNavigationModification}
                  onCloseRule={closeRule}
                  open={open}
                  rule={ruleItem.rule}
                  onDelete={() => {
                    setRuleToDelete(ruleItem.rule);
                    setShowDeletionAlert.setTrue();
                  }}
                />
              )}
            />
          ) : (
            <RulesManagerEmptySearch />
          )}
        </RulesManager.Content>
      </RulesContent>
      <RuleDeletionAlert
        title="Delete rule"
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onErrorMessage="Failed to delete"
        onConfirm={async () => {
          await deleteNavigationModification(ruleToDelete);
          setShowDeletionAlert.setFalse();
        }}
        rulePreview={<NavigationRulePreview rule={ruleToDelete} />}
      />
    </>
  );
};
